import { useMutation } from '@tanstack/react-query';

import { createProgressToast } from '@/components/common/progress-toast';
import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

import {
  isAbortError,
  ProgressStreamError,
  readProgressStream,
} from '../progress-stream';

export class ServerError extends Error {
  readonly detail?: string;

  constructor(detail?: string) {
    const message = detail?.trim();
    super(message);
    this.name = 'ServerError';
    this.detail = message || undefined;
  }
}

export enum ValidationMessageLevel {
  ERROR = 'ERROR',
  MESSAGE = 'MESSAGE',
  WARNING = 'WARNING',
}

export type DonorMismatchData = {
  existingDonorId?: string | null;
  existingRegistrationNumber?: string | null;
  existingPedigreeName?: string | null;
  existingBirthDate?: string | null;
  inputRegistrationNumber?: string | null;
  inputPedigreeName?: string | null;
  inputBirthDate?: string | null;
};

export type ValidationFieldMessage = {
  field: string;
  message: string;
};

export type ValidationInputMessage = {
  index: number;
  field: string;
  message: string;
  level: ValidationMessageLevel;
  suggestions?: string[] | null;
  correctedValue?: string | null;
  mismatchData?: DonorMismatchData | null;
};

export type BulkDonorScheduleValidationResponse = {
  fields: ValidationFieldMessage[];
  inputs: ValidationInputMessage[];
};

export type ValidateBulkDonorScheduleRequest = {
  aspirationDate?: string | null;
  aspirationSiteId?: string | null;
  fertilizationSiteId?: string | null;
  inputs: (BulkDonorScheduleInputRow | null)[];
};

export type UpsertBulkDonorScheduleRequest = {
  aspirationDate: string;
  aspirationSiteId: string;
  fertilizationSiteId: string;
  inputs: BulkDonorScheduleInputRow[];
};

export type BulkDonorScheduleInputRow = {
  accountCode: string;
  billAccountCode?: string | null;
  donorBreed: string;
  donorNameToMatch: string;
  donorRegistrationNumber?: string | null;
  pedigreeName?: string | null;
  pregnant?: boolean | null;
  birthDate?: string | null;
  programName?: string | null;
  tpi?: number | null;
  nm?: number | null;
  opuProgram?: string | null;
  tattoo?: string | null;
  received?: string | null;
  yardCode?: string | null;
  sire?: string | null;
  semen?: string | null;
  sireBreed?: string | null;
  sireRegistrationNumber?: string | null;
  collectionCode?: string | null;
  semenType?: string | null;
  donorLocation?: string | null;
  scheduleCode?: string | null;
  pool?: boolean | null;
  aspirationComment?: string | null;
  naab?: string | null;
  cane?: string | null;
  ship?: string | null;
  shipmentGroup?: string | null;
  freeze?: string | null;
  freezeMethod?: string | null;
  freezeInstructions?: string | null;
  export?: string | null;
  trypsinTreat?: boolean | null;
  nonEuCountry?: string | null;
  updateExistingDonor?: boolean | null;
};

export type FarbroBulkDonorScheduleInputRow = {
  donorNameToMatch: string;
  donorRegistrationNumber: string;
  birthDate: string;
  donorBreed: string;
  location: 'Exum' | 'Hartley';
  opuDate: string;
  naab?: string | null;
  sire?: string | null;
  semenType?: string | null;
  collectionCode?: string | null;
  priority: string;
  donorResolution?: 'USE_EXISTING' | 'CREATE_NEW' | null;
  gradeInstruction?: 'g1-g2' | 'g1-g2-g3' | 'freeze-dt-g1-g2' | null;
  matingClassification?: 'angus' | 'holstein' | 'crossbred' | 'jersey' | null;
};

export type ValidateFarbroBulkDonorScheduleRequest = {
  inputs: (FarbroBulkDonorScheduleInputRow | null)[];
};

export type ValidateFarbroBulkDonorScheduleResponse =
  BulkDonorScheduleValidationResponse & {
    duplicateIndexes: number[];
  };

type FarbroBulkDonorScheduleStreamError = {
  message: string;
};

export type UpsertFarbroBulkDonorScheduleRequest = {
  inputs: FarbroBulkDonorScheduleInputRow[];
};

export type FarbroBulkDonorScheduleProcessingResult = {
  donorSchedulesProcessed: number;
  soSchedulesCreated: number;
  chuteSchedulesCreated: number;
  successfulInputIndexes: number[];
  manualFollowUpInputIndexes: number[];
};

export type AgllcBulkDonorScheduleInputRow = {
  priority?: number | null;
  donorNameToMatch: string;
  pen?: string | null;
  donorRegistrationNumber?: string | null;
  donorBreed: string;
  birthDate?: string | null;
  psi?: number | null;
  gnm?: number | null;
  gcm?: number | null;
  pregnant?: boolean | null;
  programName?: string | null;
  opuDate?: string | null;
  opuSite?: string | null;
  sire?: string | null;
  naab?: string | null;
  sir1Psi?: number | null;
  semenType?: string | null;
  collectionCode?: string | null;
  backupSire?: string | null;
  backupNaab?: string | null;
  sir2Psi?: number | null;
  backupSemenType?: string | null;
  donorResolution?: 'USE_EXISTING' | 'CREATE_NEW' | null;
};

export type ValidateAgllcBulkDonorScheduleRequest = {
  fertilizationSiteId?: string | null;
  inputs: (AgllcBulkDonorScheduleInputRow | null)[];
};

export type ValidateAgllcBulkDonorScheduleResponse =
  BulkDonorScheduleValidationResponse & {
    duplicateIndexes: number[];
  };

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function getResponseErrorMessage(
  response: Response,
): Promise<string | undefined> {
  try {
    const body = (await response.json()) as {
      message?: string;
      detail?: string;
      title?: string;
    };
    const error = body.message || body.detail || body.title;
    if (typeof error === 'string') {
      return error;
    }
  } catch {
    // use the fallback
  }
}

export const useValidateBulkDonorSchedules = () => {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: ValidateBulkDonorScheduleRequest) {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/bulk-donor-schedule/validate`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(token),
          body: JSON.stringify(request),
        },
      );
      if (!response.ok) {
        throw new ServerError(await getResponseErrorMessage(response));
      }
      return (await response.json()) as BulkDonorScheduleValidationResponse;
    },
  });
};

export const useValidateFarbroBulkDonorSchedules = () => {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: ValidateFarbroBulkDonorScheduleRequest) {
      const abortController = new AbortController();
      const progressToast = createProgressToast({
        title: 'Validating FARBRO roster',
        label: 'Validating rows',
        errorTitle: 'FARBRO validation failed',
        onCancel: () => abortController.abort(),
      });

      try {
        const response = await fetch(
          `${getBaseUrl()}/api/v2/bulk-donor-schedule/validate/farbro`,
          {
            method: 'POST',
            credentials: 'include',
            headers: getHeaders(token),
            body: JSON.stringify(request),
            signal: abortController.signal,
          },
        );
        if (!response.ok) {
          throw new ServerError(await getResponseErrorMessage(response));
        }

        for await (const event of readProgressStream<
          ValidateFarbroBulkDonorScheduleResponse,
          FarbroBulkDonorScheduleStreamError
        >(response)) {
          switch (event.type) {
            case 'progress':
              progressToast.report(event.progress);
              break;
            case 'success':
              progressToast.close();
              return event.success;
            case 'error':
              throw new ProgressStreamError(event.error);
          }
        }

        throw new Error('Validation stream ended without a terminal event.');
      } catch (error) {
        if (isAbortError(error)) {
          progressToast.cancelled();
          throw error;
        }
        const message =
          error instanceof ProgressStreamError
            ? (error as ProgressStreamError<FarbroBulkDonorScheduleStreamError>)
                .error.message
            : error instanceof Error
              ? error.message
              : undefined;

        progressToast.error(message || 'Validation failed.');

        throw error;
      }
    },
  });
};

export const useUpsertFarbroBulkDonorSchedules = () => {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: UpsertFarbroBulkDonorScheduleRequest) {
      const abortController = new AbortController();
      const progressToast = createProgressToast({
        title: 'Processing FARBRO roster',
        label: 'Processing rows',
        errorTitle: 'FARBRO processing failed',
        onCancel: () => abortController.abort(),
      });

      try {
        const response = await fetch(
          `${getBaseUrl()}/api/v2/bulk-donor-schedule/upsert-schedules/farbro`,
          {
            method: 'POST',
            credentials: 'include',
            headers: getHeaders(token),
            body: JSON.stringify(request),
            signal: abortController.signal,
          },
        );
        if (!response.ok) {
          throw new ServerError(await getResponseErrorMessage(response));
        }

        for await (const event of readProgressStream<
          FarbroBulkDonorScheduleProcessingResult,
          FarbroBulkDonorScheduleStreamError
        >(response)) {
          switch (event.type) {
            case 'progress':
              progressToast.report(event.progress);
              break;
            case 'success':
              progressToast.close();
              return event.success;
            case 'error':
              throw new ProgressStreamError(event.error);
          }
        }

        throw new Error('Processing stream ended without a terminal event.');
      } catch (error) {
        if (isAbortError(error)) {
          progressToast.cancelled();
          throw error;
        }
        const message =
          error instanceof ProgressStreamError
            ? (error as ProgressStreamError<FarbroBulkDonorScheduleStreamError>)
                .error.message
            : error instanceof Error
              ? error.message
              : undefined;

        progressToast.error(message || 'Processing failed.');

        throw error;
      }
    },
  });
};

export type AgllcBulkDonorScheduleProcessingResult = {
  donorSchedulesProcessed: number;
  soSchedulesCreated: number;
  chuteSchedulesCreated: number;
  successfulInputIndexes: number[];
  manualFollowUpInputIndexes: number[];
};

type AgllcBulkDonorScheduleStreamError = {
  message: string;
};

export const useValidateAgllcBulkDonorSchedules = () => {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: ValidateAgllcBulkDonorScheduleRequest) {
      const abortController = new AbortController();
      const progressToast = createProgressToast({
        title: 'Validating AGLLC roster',
        label: 'Validating rows',
        errorTitle: 'AGLLC validation failed',
        onCancel: () => abortController.abort(),
      });

      try {
        const response = await fetch(
          `${getBaseUrl()}/api/v2/bulk-donor-schedule/validate/agllc`,
          {
            method: 'POST',
            credentials: 'include',
            headers: getHeaders(token),
            body: JSON.stringify(request),
            signal: abortController.signal,
          },
        );
        if (!response.ok) {
          throw new ServerError(await getResponseErrorMessage(response));
        }

        for await (const event of readProgressStream<
          ValidateAgllcBulkDonorScheduleResponse,
          AgllcBulkDonorScheduleStreamError
        >(response)) {
          switch (event.type) {
            case 'progress':
              progressToast.report(event.progress);
              break;
            case 'success':
              progressToast.close();
              return event.success;
            case 'error':
              throw new ProgressStreamError(event.error);
          }
        }

        throw new Error('Validation stream ended without a terminal event.');
      } catch (error) {
        if (isAbortError(error)) {
          progressToast.cancelled();
          throw error;
        }
        const message =
          error instanceof ProgressStreamError
            ? (error as ProgressStreamError<AgllcBulkDonorScheduleStreamError>)
                .error.message
            : error instanceof Error
              ? error.message
              : undefined;

        progressToast.error(message || 'Validation failed.');

        throw error;
      }
    },
  });
};

export const useUpsertBulkDonorSchedules = () => {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: UpsertBulkDonorScheduleRequest) {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/bulk-donor-schedule/upsert-schedules`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(token),
          body: JSON.stringify(request),
        },
      );
      if (!response.ok) {
        throw new ServerError(await getResponseErrorMessage(response));
      }
    },
  });
};

export type UpsertAgllcBulkDonorScheduleRequest = {
  fertilizationSiteId: string;
  inputs: AgllcBulkDonorScheduleInputRow[];
};

export type AgllcPriorityInput = {
  index: number;
  opuDate?: string | null;
  donorPsi?: number | null;
  sir1Psi?: number | null;
  reg?: string | null;
};

export type AgllcPriorityResult = {
  index: number;
  priority: number | null;
};

export type CalculateAgllcPrioritiesResponse = {
  priorities: AgllcPriorityResult[];
};

export const useCalculateAgllcPriorities = () => {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: { inputs: AgllcPriorityInput[] }) {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/bulk-donor-schedule/calculate-priorities/agllc`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(token),
          body: JSON.stringify(request),
        },
      );
      if (!response.ok) {
        throw new ServerError(await getResponseErrorMessage(response));
      }
      return (await response.json()) as CalculateAgllcPrioritiesResponse;
    },
  });
};

export type ApplyAgllcPriorityInput = AgllcPriorityInput & {
  donorNameToMatch?: string | null;
};

/**
 * Why a row or an existing schedule could not be ranked. Priorities are ranked over the whole OPU
 * day, so anything skipped is reported back rather than silently left alone.
 */
export type ApplyAgllcPriorityReason =
  | 'NOT_ELIGIBLE'
  | 'NO_DONOR_RECORD'
  | 'NO_SCHEDULE_ON_DATE'
  | 'MISSING_PSI'
  | 'DUPLICATE_DONOR_DATE'
  | 'AMBIGUOUS_DONOR_NAME';

export type ApplyAgllcPriorityResult = {
  index: number;
  priority: number | null;
  matchedExistingSchedule: boolean;
  reason?: ApplyAgllcPriorityReason | null;
  psiMismatch?: boolean;
};

/**
 * A schedule on one of the submitted OPU dates that could not be ranked. Its priority is cleared so
 * it cannot duplicate one of the ranks that were handed out, and it needs fixing and a re-run.
 */
export type ApplyAgllcPriorityExclusion = {
  opuDate: string;
  donorName: string;
  reason: ApplyAgllcPriorityReason;
};

export type ApplyAgllcPrioritiesResponse = {
  priorities: ApplyAgllcPriorityResult[];
  schedulesUpdated: number;
  schedulesCleared: number;
  excluded: ApplyAgllcPriorityExclusion[];
};

export const useApplyAgllcPriorities = () => {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: { inputs: ApplyAgllcPriorityInput[] }) {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/bulk-donor-schedule/apply-priorities/agllc`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(token),
          body: JSON.stringify(request),
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as ApplyAgllcPrioritiesResponse;
    },
  });
};

export const useUpsertAgllcBulkDonorSchedules = () => {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: UpsertAgllcBulkDonorScheduleRequest) {
      const abortController = new AbortController();
      const progressToast = createProgressToast({
        title: 'Processing AGLLC roster',
        label: 'Processing rows',
        errorTitle: 'AGLLC processing failed',
        onCancel: () => abortController.abort(),
      });

      try {
        const response = await fetch(
          `${getBaseUrl()}/api/v2/bulk-donor-schedule/upsert-schedules/agllc`,
          {
            method: 'POST',
            credentials: 'include',
            headers: getHeaders(token),
            body: JSON.stringify(request),
            signal: abortController.signal,
          },
        );
        if (!response.ok) {
          throw new ServerError(await getResponseErrorMessage(response));
        }

        for await (const event of readProgressStream<
          AgllcBulkDonorScheduleProcessingResult,
          AgllcBulkDonorScheduleStreamError
        >(response)) {
          switch (event.type) {
            case 'progress':
              progressToast.report(event.progress);
              break;
            case 'success':
              progressToast.close();
              return event.success;
            case 'error':
              throw new ProgressStreamError(event.error);
          }
        }

        throw new Error('Processing stream ended without a terminal event.');
      } catch (error) {
        if (isAbortError(error)) {
          progressToast.cancelled();
          throw error;
        }
        const message =
          error instanceof ProgressStreamError
            ? (error as ProgressStreamError<AgllcBulkDonorScheduleStreamError>)
                .error.message
            : error instanceof Error
              ? error.message
              : undefined;

        progressToast.error(message || 'Processing failed.');

        throw error;
      }
    },
  });
};
